import { PRIVATE_MIRROR } from '../lib/feedContext';
/**
 * Дверь перед сканером: невошедший получает вход, а не урезанную ленту.
 *
 * До 12.08.2026 сканер открывался всем, а невошедшему сервер вырезал имена
 * площадок и купоны. Это была витрина: «смотри, вилки есть, названий не дам».
 * Владелец её закрыл словами «никакой ленты, страница входа должна быть» —
 * лента и есть продукт, и показывать её половиной значит отдавать половину.
 *
 * Решение принимается ЗДЕСЬ, а не внутри сканера, по одной причине: сканер при
 * запуске поднимает сокет и тянет метаданные, и сделать это, чтобы через
 * полсекунды нарисовать поверх дверь, — значит показать человеку мигание ленты,
 * которую он не имеет права видеть.
 *
 * Пока сервер не ответил, в `#root` стоит только шапка. Это доли секунды
 * одного запроса. Пустой корень честнее мигания ленты или двери — выкат 13.08
 * откатил исправный подвал, потому что проверка видит пустой корень и считает,
 * что приложение не смонтировалось. Шапка остаётся: проверка читает «FORQ».
 * Человеку до ответа на первом заходе ленты закрывает холст оболочка из
 * `index.html` (`#boot`). Живую ленту по возврату с конторы ею не накрываем.
 *
 * Контур, где входа нет вовсе (`possible === false`: зеркало без бота), проходит
 * насквозь. Запереть дом, ключ от которого не выдаётся, — не защита, а поломка:
 * там лента открыта намеренно, и держится это не дверью, а стражем утечки.
 */

import { useEffect } from 'react';

import { useSession } from '../access/session';
import { bootReady } from './boot';
import { Landing } from './Landing';
import { StatDock } from './StatDock';
import { StatNotice } from './StatNotice';
import { TicketDock } from './tickets/TicketDock';
import { primeLiveFeed, releaseGateFeed } from './liveFeed';
import { loadMeta } from './meta';
import { isDry } from './route';
import { useScreen } from './store';

export function Gate({ children }: { children: React.ReactElement }): React.ReactElement {
  const known = useSession((s) => s.known);
  const access = useSession((s) => s.me.access);
  const possible = useSession((s) => s.me.possible);

  useEffect(() => {
    // Холст ставится до всякого решения: без него доля секунды до ответа
    // сервера светит белым листом, и выглядит это как сломанная загрузка.
    document.body.classList.add('nx-body');
    // /dry тянет свои источники сам (`DRY_TAPES`). Сокет /app и кэш прошлой вкладки сюда нельзя:
    // иначе на сухом экране чужие вилки (Pinnacle × Polymarket с /v2).
    if (isDry(location.pathname)) {
      useScreen.getState().apply(new Map());
    } else {
      primeLiveFeed();
    }
    if (useScreen.getState().framed || isDry(location.pathname)) bootReady();
    // /v1/me под нагрузкой уезжает на секунды. Оболочка из index.html не
    // имеет права ждать дверь: человек видит вечное «собираем» и думает,
    // что сервис мёртв.
    const lift = window.setTimeout(bootReady, 700);
    void useSession.getState().load();
    void loadMeta().then((meta) => {
      if (meta !== null) useScreen.getState().setMeta(meta);
    });
    return () => {
      window.clearTimeout(lift);
      releaseGateFeed();
    };
  }, []);

  useEffect(() => {
    if (!known) return;
    if (useScreen.getState().framed) {
      bootReady();
      return;
    }
    const hide = (): void => {
      requestAnimationFrame(() => requestAnimationFrame(() => bootReady()));
    };
    hide();
    window.addEventListener('pageshow', hide);
    return () => window.removeEventListener('pageshow', hide);
  }, [known]);

  // Пускает не вход, а доступ. Пока /v1/me не ответил — рисуем ленту
  // (кэш или «собираем» внутри Screen), а не голую шапку под оболочкой.
  if (known && possible && !access) return <Landing />;
  return (
    <>
      {children}
      {!PRIVATE_MIRROR && <><StatDock /><TicketDock /><StatNotice /></>}
    </>
  );
}
